/**
 * Overlay Layer Component
 *
 * Renders overlay elements as positioned DOM nodes with CSS transforms.
 * Matches storefront rendering logic exactly.
 */

import { useMemo } from 'react';
import type { OverlayElement } from '../types';

interface OverlayLayerProps {
  overlay: OverlayElement;
  isSelected: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
  onResizePointerDown?: (e: React.PointerEvent) => void;
  onRotatePointerDown?: (e: React.PointerEvent) => void;
  imageDimensions?: { width: number; height: number } | null;
  onImageLoad?: (img: HTMLImageElement) => void;
}

export function OverlayLayer({
  overlay,
  isSelected,
  onPointerDown,
  onResizePointerDown,
  onRotatePointerDown,
  imageDimensions,
  onImageLoad,
}: OverlayLayerProps) {
  const transformStyle = useMemo(() => {
    const { xPct, yPct, scale, rotationDeg, zIndex } = overlay.transform;
    return {
      position: 'absolute' as const,
      left: `${xPct}%`,
      top: `${yPct}%`,
      transform: `translate(-50%, -50%) scale(${scale}) rotate(${rotationDeg}deg)`,
      zIndex,
      pointerEvents: 'auto' as const,
      touchAction: 'none' as const,
      userSelect: 'none' as const,
    };
  }, [overlay.transform]);

  const content = useMemo(() => {
    if (overlay.type === 'image' && overlay.assetRef) {
      return (
        <img
          src={overlay.assetRef}
          alt=""
          draggable={false}
          className="max-w-none"
          style={{
            width: imageDimensions?.width ? `${imageDimensions.width}px` : '80px',
            height: imageDimensions?.height ? `${imageDimensions.height}px` : '80px',
            display: 'block',
            objectFit: 'contain',
          }}
          onLoad={(e) => {
            if (onImageLoad) {
              onImageLoad(e.currentTarget);
            }
          }}
        />
      );
    } else if (overlay.type === 'text' && overlay.content) {
      return <span className="whitespace-nowrap">{overlay.content}</span>;
    } else if (overlay.type === 'button') {
      if (overlay.href) {
        return (
          <a
            href={overlay.href}
            className="inline-block px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            onClick={(e) => {
              // Prevent navigation in builder
              e.preventDefault();
            }}
          >
            {overlay.content || 'Button'}
          </a>
        );
      } else {
        return (
          <span className="inline-block px-4 py-2 bg-gray-300 text-gray-700 rounded-md">
            {overlay.content || 'Button'}
          </span>
        );
      }
    }
    return null;
  }, [overlay, imageDimensions, onImageLoad]);

  return (
    <div
      style={transformStyle}
      onPointerDown={onPointerDown}
      className={isSelected ? 'outline-2 outline-blue-500 outline' : ''}
    >
      {content}
      {isSelected && (
        <>
          {/* Resize handle (bottom-right) */}
          {onResizePointerDown && (
            <div
              role="button"
              aria-label="Resize handle"
              className="absolute bottom-0 right-0 w-4 h-4 bg-blue-500 border-2 border-white rounded-full cursor-nwse-resize transform translate-x-1/2 translate-y-1/2"
              style={{ zIndex: overlay.transform.zIndex + 1 }}
              onPointerDown={(e) => {
                e.stopPropagation();
                if (onResizePointerDown) onResizePointerDown(e);
              }}
            />
          )}
          {/* Rotate handle (top center) */}
          {onRotatePointerDown && (
            <div
              role="button"
              aria-label="Rotate handle"
              className="absolute left-1/2 w-4 h-4 bg-blue-500 border-2 border-white rounded-full cursor-grab transform -translate-x-1/2"
              style={{ zIndex: overlay.transform.zIndex + 1, top: '-20px' }}
              onPointerDown={(e) => {
                e.stopPropagation();
                if (onRotatePointerDown) onRotatePointerDown(e);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
